import React, { useState, useEffect } from 'react'
import {
  InformationCircleIcon,
  CogIcon,
  DocumentTextIcon,
  CameraIcon,
  ClockIcon,
  ServerIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'

function PatronConfiguration() {
  const [configData, setConfigData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    loadConfiguration()
  }, [])

  const loadConfiguration = async () => {
    try {
      setLoading(true)
      setError(null)

      const res = await authFetch(getLocalApiUrl('/api/configuration'))
      if (!res.ok) throw new Error(`Server responded with ${res.status}`)
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Unknown error')

      setConfigData(json.data)
      setLastRefresh(new Date())
    } catch (err) {
      setError(err.message || 'Failed to load configuration data')
    } finally {
      setLoading(false)
    }
  }

  const refreshConfiguration = async () => {
    setRefreshing(true)
    await loadConfiguration()
    setRefreshing(false)
  }

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'system':
        return <ServerIcon className="w-6 h-6 text-blue-600" />
      case 'profile settings':
        return <DocumentTextIcon className="w-6 h-6 text-green-600" />
      case 'document processing':
        return <DocumentTextIcon className="w-6 h-6 text-purple-600" />
      case 'biometric settings':
        return <CameraIcon className="w-6 h-6 text-red-600" />
      case 'security':
        return <ShieldCheckIcon className="w-6 h-6 text-orange-600" />
      case 'touch points':
        return <CogIcon className="w-6 h-6 text-indigo-600" />
      default:
        return <InformationCircleIcon className="w-6 h-6 text-gray-600" />
    }
  }

  const formatValue = (value, type) => {
    switch (type) {
      case 'BOOLEAN':
        return value === 'true' ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Enabled
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Disabled
          </span>
        )
      case 'LIST':
        return (
          <div className="flex flex-wrap gap-1">
            {value.split(',').map((item, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                {item.trim()}
              </span>
            ))}
          </div>
        )
      case 'NUMBER':
      case 'FLOAT':
        return <span className="font-mono text-sm">{value}</span>
      case 'VERSION':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            v{value}
          </span>
        )
      case 'JSON':
        try {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return (
              <div className="text-left font-mono text-xs bg-gray-50 rounded-md p-3 max-w-md overflow-x-auto">
                {Object.entries(parsed).map(([k, v]) => (
                  <div key={k} className="py-0.5 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-600">{k}:</span>{' '}
                    <span className="text-gray-900">{String(v)}</span>
                  </div>
                ))}
              </div>
            )
          }
        } catch (_) {}
        return (
          <pre className="text-xs bg-gray-50 rounded-md p-3 max-w-md overflow-x-auto whitespace-pre-wrap break-words font-mono">
            {value}
          </pre>
        )
      default:
        return <span className="text-sm break-words max-w-md">{value}</span>
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'BOOLEAN':
        return <ShieldCheckIcon className="w-4 h-4 text-gray-400" />
      case 'NUMBER':
      case 'FLOAT':
        return <CircleStackIcon className="w-4 h-4 text-gray-400" />
      case 'LIST':
      case 'JSON':
        return <DocumentTextIcon className="w-4 h-4 text-gray-400" />
      default:
        return <InformationCircleIcon className="w-4 h-4 text-gray-400" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <CircleStackIcon className="h-12 w-12 mx-auto mb-4 text-red-600" />
          <p className="text-red-600 mb-2">Failed to load configuration</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button 
            onClick={loadConfiguration}
            className="mt-4 btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">System Configuration</h1>
            <p className="text-gray-600">
              View and monitor current system configuration settings and parameters.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {lastRefresh && (
              <div className="text-sm text-gray-500">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
            )}
            <button 
              onClick={refreshConfiguration}
              disabled={refreshing}
              className="btn-secondary"
            >
              {refreshing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Categories */}
      <div className="grid grid-cols-1 gap-6">
        {configData.map((category) => (
          <div key={category.category} className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                {getCategoryIcon(category.category)}
                <h2 className="ml-3 text-lg font-medium text-gray-900">
                  {category.category}
                </h2>
                <span className="ml-2 text-sm text-gray-500">
                  ({category.settings.length} settings)
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {category.settings.map((setting) => (
                  <div key={setting.name} className={`flex justify-between py-3 border-b border-gray-100 last:border-b-0 ${setting.type === 'JSON' ? 'items-start' : 'items-center'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        {getTypeIcon(setting.type)}
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            {setting.description}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {setting.name} ({setting.type})
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className={`ml-4 flex-shrink-0 ${setting.type === 'JSON' ? 'min-w-0' : ''}`}>
                      {formatValue(setting.value, setting.type)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* System Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">System Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <ServerIcon className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-blue-600 font-medium">Server Status</p>
                <p className="text-lg font-bold text-blue-900">Online</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <CircleStackIcon className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-green-600 font-medium">Database</p>
                <p className="text-lg font-bold text-green-900">Connected</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center">
              <CogIcon className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm text-purple-600 font-medium">API Version</p>
                <p className="text-lg font-bold text-purple-900">1.0.0.1</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatronConfiguration